import { useState, useEffect, useCallback } from "react";
import axiosInstance, { silentRequest } from "../utils/axiosConfig";
import { toast } from "react-toastify";

export interface Notification {
  id: string;
  title: string;
  message: string;
  timestamp: string;
  status: "unread" | "read";
  /** `notice` = company-wide; `user` = private (e.g. letters) */
  source: "notice" | "user";
  /** Deep-link for private notifications */
  link?: string;
}

const useNotifications = (userId: string | undefined) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      const [noticesRes, userNotifsRes] = await Promise.all([
        silentRequest.get<{ notices?: any[] }>(`${backendUrl}/api/notices`),
        silentRequest.get<{ data?: any[]; unreadCount?: number }>(
          `${backendUrl}/api/user-notifications/me`,
        ),
      ]);

      const noticeItems: Notification[] = (noticesRes.data?.notices || []).map(
        (notice: any) => ({
          id: notice._id,
          title: notice.subject,
          message: notice.paragraph,
          timestamp: notice.date || notice.createdAt,
          status: String(notice.status || "Unread").toLowerCase() as
            | "unread"
            | "read",
          source: "notice" as const,
        }),
      );

      const userItems: Notification[] = (userNotifsRes.data?.data || []).map(
        (n: any) => ({
          id: n._id,
          title: n.title,
          message: n.message,
          timestamp: n.createdAt,
          status: String(n.status || "Unread").toLowerCase() as
            | "unread"
            | "read",
          source: "user" as const,
          link: n.link || undefined,
        }),
      );

      const merged = [...userItems, ...noticeItems].sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );

      setNotifications(merged);
      setUnreadCount(merged.filter((n) => n.status === "unread").length);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, backendUrl]);

  const markAsRead = async (notificationId: string) => {
    const target = notifications.find((n) => n.id === notificationId);
    if (!target || target.status === "read") return;

    try {
      if (target.source === "user") {
        await axiosInstance.patch(
          `${backendUrl}/api/user-notifications/${notificationId}/read`,
          {},
          { withCredentials: true },
        );
      } else {
        await axiosInstance.patch(
          `${backendUrl}/api/notices/${notificationId}/status`,
          { status: "Read" },
          { withCredentials: true },
        );
      }

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, status: "read" } : n,
        ),
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      toast.error("Failed to update notification status");
    }
  };

  const markAllAsRead = async () => {
    try {
      await Promise.all([
        axiosInstance.patch(
          `${backendUrl}/api/notices/mark-all-read`,
          {},
          { withCredentials: true },
        ),
        axiosInstance.patch(
          `${backendUrl}/api/user-notifications/mark-all-read`,
          {},
          { withCredentials: true },
        ),
      ]);

      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          status: "read" as const,
        })),
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      toast.error("Failed to update notifications");
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  };
};

export default useNotifications;
